<template>
  <aside id="sidebar"
    class="fixed bg-primary py-[30px] flex flex-col duration-300 bg-origin min-h-dvh overflow-hidden z-40 top-0 bottom-0 left-0 shadow-2xl">
    <div class="flex items-center gap-[8px] cursor-pointer px-5">
      <img src="@/assets/images/logo.png" class="w-[35px] h-[35px]" />
      <div class="text-white font-[IBM_Plex_Sans_Thai]">
        <div class="text-[10px] text-nowrap font-semibold">
          ระบบภูมิสารสนเทศทางสาธารณสุข
        </div>
        <div class="text-[10px] text-[#FFF] text-nowrap font-semibold">
          สำหรับศูนย์ปฏิบัติการภาวะฉุกเฉิน (จีโอเฮลท์ ศปฉ.)
        </div>
      </div>
      <button @click="closeSidebar()"
        class="lg:hidden inline ml-auto border border-white rounded-md min-h-[42px] min-w-[42px]">
        <CloseIcon class="mx-auto text-white w-6 h-6" />
      </button>
    </div>
    <div id="scrollBar" class="text-md px-5 overflow-y-auto my-9">
      <ul class="overflow-hidden">
        <li v-for="menu in visibleMenus" :key="menu.name" class="rounded-md hover:bg-[#841D69]">
          <!-- เมนูเดี่ยว -->
          <RouterLink v-if="!menu.childRoutes" :to="menu.effectiveRoute"
            activeClass="!text-primary bg-white !stroke-primary !fill-primary" @click="onMenuClick(menu)"
            class="flex items-center gap-2.5 h-12 rounded-md my-2 pl-[10px] text-white">
            <component :is="menu.icon" class="!fill-primary w-[22px]" />
            <span class="PC_Body2-R">{{ menu.name }}</span>
          </RouterLink>

          <!-- เมนูที่มีลูก -->
          <div v-else>
            <div :ref="'parent ' + menu.name"
              class="parent flex items-center pl-[10px] gap-2.5 h-12 rounded-sm mt-2 text-white cursor-pointer"
              @click="toggleMenu(menu.name)">
              <component :is="menu.icon" class="!fill-primary w-[22px]" />
              <span class="PC_Body2-R">{{ menu.name }}</span>
              <CaretDownIcon class="caret-icon ml-auto mr-2.5 duration-300 w-6 h-6" />
            </div>

            <ul :ref="'child ' + menu.name" class="child hidden w-full bg-[#A9408D] rounded-b-md">
              <li v-for="childRoute in menu.childRoutes" :key="childRoute.effectiveRoute ||
                childRoute.route ||
                childRoute.name
                ">
                <RouterLink :to="childRoute.effectiveRoute" activeClass="text-white bg-[#841D69]"
                  @click="onMenuClick(menu)"
                  class="flex items-center gap-2.5 h-12 mb-2 pl-[10px] text-primary-light hover:text-white">
                  <span class="PC_Body2-R">{{ childRoute.name }}</span>
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
    <div @click="logout" class="w-full bg-primary mt-auto px-5" style="justify-self: anchor-center">
      <button id="logout"
        class="w-full flex justify-center items-center h-12 border rounded-md border-white text-white text-sm">
        <span>ออกจากระบบ</span>
      </button>
    </div>
  </aside>
</template>

<script>
import axios from "axios";
import CaretDownIcon from "@/assets/icons/caretDown.icon.vue";
import peopleUnderShelterIcon from "@/assets/icons/people-under-shelter.icon.vue";
import VueCookies from "vue-cookies";
import DashboardIcon from "@/assets/icons/dashboard.icon.vue";
import { useOnlineStatus } from '@/composables/useOnlineStatus';

// สิทธิ์/โรล
import { ROLES } from "@/components/Utilities/javascripts/permissions";

// Pinia
import { mapStores } from "pinia";
import { useAuthStore } from "@/pinia/authStore";

export default {
  components: { CaretDownIcon, peopleUnderShelterIcon, DashboardIcon },
  props: {
    closeSidebar: {
      type: Function,
    },
  },
  setup() {
    const { isOnline } = useOnlineStatus()
    return { isOnline }
  },
  data() {
    return {
      menuOpen: true,
      menus: [],
      offlineMenus: [
        {
          name: "แบบประเมินออฟไลน์",
          icon: "DashboardIcon",
          route: "/offline-assessment",
        },
      ],

      defaultMenus: [
        {
          name: "แดชบอร์ด",
          icon: "DashboardIcon",
          route: "/dashboard",
          perm: "VIEW_DASHBOARD",
        },
        {
          name: "ภัยพิบัติ",
          icon: "DisasterIcon2",
          route: "/disaster",
          perm: "VIEW_DISASTER",
          childRoutes: [
            { name: "อุทกภัย", route: "/disaster/flood" },
            { name: "ดินถล่ม", route: "/disaster/landslide" },
            { name: "ไฟป่า", route: "/disaster/wildfire" },
            { name: "หมอกควัน", route: "/disaster/smog" },
            { name: "แผ่นดินไหว", route: "/disaster/earthquake" },
            { name: "พายุฤดูร้อน", route: "/disaster/summer-storm" },
            { name: "อัคคีภัย", route: "/disaster/fire" },
            { name: "โรคระบาด", route: "/disaster/epidemic" },
            {
              name: "ประวัติการประกาศ / อัปเดต / ยุติ ภัยพิบัติ",
              route: "/disaster/history",
            },
          ],
        },
        {
          name: "ศูนย์พักพิง",
          icon: "ShelterIcon",
          route: "/shelter", // default สำหรับ role อื่น
          perm: "VIEW_SHELTER",
          routeByRole: {
            [ROLES.staff]: (ud) => `/shelter/${ud.shelter_name}`,
            //   ud?.shelter_name
            //     ? `/shelter/${ud.shelter_name}`
            //     : "/shelter",
          },
        },
        {
          name: "ทีมปฎิบัติงาน",
          icon: "MedicalKitIcon",
          perm: "VIEW_MEDICAL",
          childRoutes: [
            {
              name: "ตารางการปฎิบัติงานของทีมเจ้าหน้าที่",
              route: "/medical-team/checkup-schedule",
            },
            {
              name: "รายชื่อทีมเจ้าหน้าที่",
              route: "/medical-team/team",
              roles: [ROLES.admin],
            },
          ],
        },
        {
          name: "คำร้องเหตุฉุกเฉิน",
          icon: "EmergencyReqIcon",
          perm: "VIEW_EMERGENCY_REQ",
          childRoutes: [
            {
              name: "คำร้องขอ",
              route: "/emergency-requests/requests",
              roles: [ROLES.admin],
            },
            {
              name: "รายการรอช่วยเหลือ",
              route: "/emergency-requests/waiting-lists-for-help",
            },
          ],
        },
        {
          name: "ประชากร",
          icon: "peopleUnderShelterIcon",
          perm: "VIEW_POPULATION",
          childRoutes: [
            { name: "ผู้ประสบภัย", route: "/population/victims" },
            {
              name: "ระยะหลังเกิดภัยพิบัติ",
              route: "/population/post-disaster-period",
            },
            { name: "ประชากรทั้งหมด", route: "/population/total-population" },
          ],
        },
        {
          name: "เจ้าหน้าที่",
          icon: "OfficerIcon",
          perm: "VIEW_OFFICER",
          childRoutes: [
            { name: "รายชื่อเจ้าหน้าที่", route: "/officer" },
            {
              name: "รายการรออนุมัติ",
              route: "/pending-approval-list",
              roles: [ROLES.admin],
            },
            {
              name: "ตั้งค่าตำแหน่งงาน",
              route: "/job-position",
              roles: [ROLES.admin],
            },
            {
              name: "ตั้งค่าหน่วยงาน",
              route: "/department",
              roles: [ROLES.admin],
            },
          ],
        },
        {
          name: "ทรัพยากรกลาง",
          icon: "ResourcesIcon",
          route: "/resource",
          perm: "VIEW_RESOURCES",
        },
        {
          name: "ประชาสัมพันธ์",
          icon: "PublicRelationIcon",
          perm: "VIEW_PUBLIC_REL",
          childRoutes: [
            { name: "บอร์ดประชาสัมพันธ์", route: "/public-relations" },
            {
              name: "ไลน์ประชาสัมพันธ์",
              route: "/public-relations-line",
              roles: [ROLES.admin],
            },
          ],
        },
        {
          name: "แบบประเมินออฟไลน์",
          icon: "DashboardIcon",
          perm: "VIEW_OFFLINE",
          route: "/offline-assessment",
          // roles: [ROLES.admin, ROLES.team_leader, ROLES.team_member],
        },
      ],
      activeMenu: null,
    };
  },

  computed: {
    ...mapStores(useAuthStore),

    visibleMenus() {
      if (this.isOnline === false) {
        this.menus = this.offlineMenus;
      } else {
        this.menus = this.defaultMenus;
      }
      const can = (perm) => (!perm ? true : this.authStore.can(perm));

      // ใช้ role แบบสตริง (ตามที่คุณใช้อยู่)
      const currentRole = this.authStore.userData?.role ?? ROLES.guest;
      const allowedByRole = (roles) =>
        !roles || (Array.isArray(roles) && roles.includes(currentRole));

      // ถ้าคุณมี authStore.hasRole(roles) อยู่แล้ว จะยิ่งสั้น:
      // const allowedByRole = (roles) => !roles || this.authStore.hasRole(roles);

      const resolveRoute = (item) => {
        const roleKey = currentRole; // 'admin' | 'staff' | ...
        const override = item?.routeByRole?.[roleKey];
        if (override) {
          if (typeof override === "function")
            return override(this.authStore.userData);
          if (typeof override === "string") return override;
        }
        return item.route || "/";
      };

      const cloned = this.menus.map((m) => {
        const item = { ...m };
        if (item.route || item.routeByRole)
          item.effectiveRoute = resolveRoute(item);

        if (Array.isArray(m.childRoutes)) {
          item.childRoutes = m.childRoutes
            .map((c) => {
              const child = { ...c, perm: c.perm || m.perm };
              if (child.route || child.routeByRole)
                child.effectiveRoute = resolveRoute(child);
              return child;
            })
            // 🔥 กรองด้วยทั้ง permission และ role ของ “เมนูย่อย”
            .filter((c) => can(c.perm) && allowedByRole(c.roles));
        }
        return item;
      });

      // 🔥 กรอง parent ด้วย perm + role ของ “เมนูพ่อ” ด้วย
      return cloned.filter((m) => {
        if (!can(m.perm)) return false;
        if (!allowedByRole(m.roles)) return false;
        if (Array.isArray(m.childRoutes)) return m.childRoutes.length > 0;
        return true;
      });
    },
  },

  mounted() {
    this.expandMenuByCurrentRoute();
  },

  watch: {
    isOnline(newVal) {
      console.log("online changed in sidebar:", newVal ? "ONLINE" : "OFFLINE");      
      if (!newVal) {
        this.menus = this.offlineMenus;
      } else {
        this.menus = this.defaultMenus;
        this.authStore.getProfile();
        console.log('gg');
        
      }
    },
    "$route.path"() {
      this.expandMenuByCurrentRoute();
    },
  },

  methods: {
    toggleMenu(tabName) {
      // ปิดเมนูอื่น
      this.visibleMenus.forEach((menu) => {
        if (
          menu.childRoutes &&
          menu.name !== tabName &&
          this.$refs["child " + menu.name] &&
          !this.$refs["child " + menu.name][0].classList.contains("hidden")
        ) {
          this.$refs["parent " + menu.name][0].classList.remove(
            "bg-white",
            "!text-primary"
          );
          this.$refs["parent " + menu.name][0].children[2].classList.remove(
            "rotate-180"
          );
          this.$refs["child " + menu.name][0].classList.add("hidden");
        }
      });

      // toggle เมนูที่กด
      if (this.activeMenu !== tabName) {
        this.$refs["parent " + tabName][0].classList.add(
          "bg-white",
          "!text-primary"
        );
        this.$refs["parent " + tabName][0].children[2].classList.add(
          "rotate-180"
        );
        this.$refs["child " + tabName][0].classList.remove("hidden");
        this.activeMenu = tabName;
      } else {
        this.$refs["parent " + tabName][0].classList.remove(
          "bg-white",
          "!text-primary"
        );
        this.$refs["parent " + tabName][0].children[2].classList.remove(
          "rotate-180"
        );
        this.$refs["child " + tabName][0].classList.add("hidden");
        this.activeMenu = null;
      }
    },

    expandMenuByCurrentRoute() {
      const current = this.$route.path;
      for (const menu of this.visibleMenus) {
        if (menu.childRoutes) {
          const match = menu.childRoutes.find(
            (c) => c.effectiveRoute === current
          );
          if (match) {
            this.$nextTick(() => {
              if (this.activeMenu !== menu.name) this.toggleMenu(menu.name);
            });
            break;
          }
        }
      }
    },

    // logout() {
    //   VueCookies.remove("token");
    //   this.$router.push("/login");
    // },
    async logout() {
      try {
        const response = await axios.post("/logout");
        if (response.status === 200) {
          VueCookies.remove("token");
          this.$router.push("/login");
        }
      } catch (error) {
        localStorage.removeItem("userData");
        // console.log(error);
      }


    },

    closeAllMenus() {
      this.visibleMenus.forEach((menu) => {
        if (menu.childRoutes && this.$refs["child " + menu.name]) {
          this.$refs["parent " + menu.name][0].classList.remove(
            "bg-white",
            "!text-primary"
          );
          this.$refs["parent " + menu.name][0].children[2].classList.remove(
            "rotate-180"
          );
          this.$refs["child " + menu.name][0].classList.add("hidden");
        }
      });
      this.activeMenu = null;
    },

    onMenuClick(menu) {
      if (menu.name === "ศูนย์พักพิง") {
        const ud = this.authStore.userData;
        const data = {
          ...ud,
          id: ud.shelter_id,
        };
        if (ud?.shelter_id != null) {
          sessionStorage.setItem("shelterID", JSON.stringify(data));
        }
      }
      this.closeAllMenus();
      window.innerWidth < 1024 ? this.closeSidebar() : "";
    },
  },
};
</script>

<style scoped>
#scrollBar::-webkit-scrollbar {
  width: 6px;
}

#scrollBar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

#scrollBar::-webkit-scrollbar-track {
  background: transparent;
}
</style>
